import { useCallback, useState, useEffect, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import { SettingsItem } from "components/layout/settingsItem/SettingsItem";
import { Dialog, DialogProps } from "components/common/dialog/Dialog";

import * as requestUrl from "constants/request";
import { extractError } from "utility/utils";
import { scopes } from "constants/idp-scopes";

import { useRequest } from "hooks/useRequest";
import { useRootDispatch, useRootSelector } from "store/hooks";
import { updateSlide } from "store/common/common-slice";
import { useAddNotification } from "hooks";

import classes from "./Settings.module.scss";
import LoadTranslation from "components/common/LoadTranslation";
import { FIRSTECH_MFA_ENABLED } from "constants/idp-claims";
import { IdToken } from "@auth0/auth0-spa-js";
import { resetUser } from "store/user/user-slice";
import { ReAuthContext } from "context/ReAuthContext";

export const Settings = () => {
  const { t } = useTranslation(["account", "common"]);

  const { loginWithRedirect, logout, getIdTokenClaims } = useAuth0();
  const { state: reAuthState, dispatch: reAuthDispatch } =
    useContext(ReAuthContext);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogProps>({});

  const { client } = useRootSelector((state) => state.config);
  const { request } = useRequest(scopes.users.writeUser);
   const { request: paymentCenterRequest } = useRequest(
    scopes.paymentCenter.write,
  );
  const dispatch = useRootDispatch();
  const addNotification = useAddNotification();

  const [tokenClaims, setTokenClaims] = useState(
    undefined as unknown as IdToken,
  );

  const handleReauthentication = (
    isReauthenticated: boolean = false,
    purpose: string = "",
  ) => {
    if (!isReauthenticated) return;
    switch (purpose) {
      case "EditAccountDetails":
        accountDetailsHandler();
        break;
      case "DeleteAccount":
        deleteAccountHandler();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const getClaims = async () => {
      try {
        const claims = await getIdTokenClaims();
        setTokenClaims(claims as IdToken);
      } catch (e) {
        console.log(e);
      }
    };
    getClaims();

    handleReauthentication(reAuthState.isReauthenticated, reAuthState.purpose);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mfaEnabled = tokenClaims?.[FIRSTECH_MFA_ENABLED];
  const twoFacSecondary = mfaEnabled
    ? t("account:mfa_enabled")
    : t("account:mfa_disabled");

  const dialogHandler = useCallback((close: boolean) => {
    setOpenDialog(close);
  }, []);

  const deleteAction = () => {
    request(requestUrl.FT_USERS, {
      method: "DELETE",
    })
     .then(() => {
          return paymentCenterRequest(requestUrl.FT_ACCOUNT_LOOKUP, {
            method: "DELETE",
          });
        })
      .then(() => {
        logout({
          logoutParams: {
            returnTo: `${window.location.origin}/${client.customerKey}`,
          },
        });
      })
      .catch((err) => {
        const errKey = extractError(err);
        addNotification({
          messages: errKey,
          type: "ERROR",
          closable: true,
        });
      });
  };

  const handleDeleteReauthentication = () => {
    const returnTo = window.location.pathname + window.location.search;
    reAuthDispatch({
      type: "SET_REAUTH",
      payload: { purpose: "DeleteAccount" },
    });

    loginWithRedirect({
      authorizationParams: {
        screen_hint: "login",
        prompt: "login",
        redirect_uri: `${window.location.origin}/${client.customerKey}/dashboard/settings`,
      },
      appState: {
        returnTo: returnTo,
      },
    });
  };

  const handleDeleteAccount = () => {
    reAuthDispatch({ type: "RESET_REAUTH" });
    setDialogProps({
      icon: "delete_forever",
      children: t("account:delete_dialog_message"),
      type: "warning",
      primaryActionLabel: t("common:continue_label"),
      showSecondaryAction: true,
      primaryActionHandler: deleteAction,
    });
    setOpenDialog(true);
  };

  const deleteAccountHandler = () => {
    if (!reAuthState.isReauthenticated || reAuthState.purpose === "") {
      handleDeleteReauthentication();
    } else {
      handleDeleteAccount();
    }
  };

  const accountDetailsHandler = () => {
    dispatch(updateSlide({ id: "AccountDetails" }));
  };

  const logoutAction = () => {
    dispatch(resetUser());
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/${client.customerKey}`,
      },
    });
  };

  const resetPasswordHandler = () => {
    // Call the users/reset-password endpoint
    request(requestUrl.FT_USERS_RESETPASSWORD, {
      method: "POST",
    })
      .then((response) => {
        setDialogProps({
          icon: "inbox",
          children: `${t("account:reset_dialog_message")}`,
          primaryActionLabel: t("common:done"),
          primaryActionHandler: logoutAction,
          showSecondaryAction: false,
        });
        // Need to trigger a notification here
        setOpenDialog(true);
      })
      .catch((err) => {
        // Throw an error message
        console.log(err);
        const errKey = extractError(err);
        addNotification({
          messages: errKey,
          type: "ERROR",
          closable: true,
        });
      });
  };

  const updateTwoFactor = () => {
    // Call the users/mfa endpoint
    request(requestUrl.FT_USERS_MFA, {
      headers: {
        "content-type": "text/plain",
      },
      data: `${!mfaEnabled}`,
      method: "PUT",
    }).then(() => {
      // Force the user to reauthenticate when making any MFA update
      logout({
        logoutParams: {
        returnTo: `${window.location.origin}/${client.customerKey}`,
        },
      });
    });
  };

  const twoFactorHandler = async () => {
    setDialogProps({
      icon: "info",
      children: mfaEnabled
        ? `${t("account:mfa_two_factor_enable_popup_msg")}`
        : `${t("account:mfa_two_factor_disable_popup_msg")}`,
      primaryActionLabel: t("common:okay_label"),
      showSecondaryAction: true,
      primaryActionHandler: updateTwoFactor,
    });
    // Need to trigger a notification here
    setOpenDialog(true);
  };

  return (
    <>
      <div className={classes.SettingsContainer}>
        <div>
          <h2 className={classes.Heading}>{t("account:settings_label")}</h2>
        </div>
        <div className={classes.GridContainer}>
          <SettingsItem
            clickHandler={accountDetailsHandler}
            icon="account_balance"
            title={t("account:account_details")}
            secondaryText={t("account:account_details_information")}
          ></SettingsItem>
          {
            // If the client has MFA user selectable (2), show the card
            // to configure MFA.
            client.mfaUserPermissions === 2 ? (
              <SettingsItem
                clickHandler={twoFactorHandler}
                icon="verified_user"
                title={t("account:two_factor_verification")}
                secondaryText={twoFacSecondary}
              ></SettingsItem>
            ) : null
          }
          <SettingsItem
            clickHandler={deleteAccountHandler}
            icon="delete_forever"
            title={t("account:delete_title")}
            secondaryText={t("account:delete_account_information")}
          ></SettingsItem>
          <SettingsItem
            clickHandler={resetPasswordHandler}
            icon="restart_alt"
            title={t("account:reset_title")}
            secondaryText={t("account:reset_password_information")}
          ></SettingsItem>
        </div>
      </div>
      {openDialog ? (
        <Dialog
          icon={dialogProps.icon}
          dialogClosed={dialogHandler}
          primaryActionLabel={dialogProps.primaryActionLabel}
          primaryActionHandler={dialogProps.primaryActionHandler}
          showSecondaryAction={dialogProps.showSecondaryAction}
          closeIcon={true}
          type={dialogProps.type}
        >
          {dialogProps.children}
        </Dialog>
      ) : null}
      <LoadTranslation keys={["account_next"]} />
    </>
  );
};
